import React, { useEffect, useMemo, useState } from 'react';
import * as printify from '../api/printifyApi';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { getProperties } from '../api/properties';
import { getIntercomConfigs, IntercomConfig } from '../api/intercomConfig';
import { AddressAutocomplete } from '../components/ui/AddressAutocomplete';
import { signShopOrdersApi } from '../api/signShopOrders';
import { SignDesigner, parseVariantAspect } from '../components/signshop/SignDesigner';
import { DesignStartChooser } from '../components/signshop/DesignStartChooser';
import { visitorOrigin } from '../utils/surface';
import {
  SignShopFilters,
  SignShopToolbar,
  SignShopEmptyState,
  applyFilterSortSearch,
  EMPTY_FILTERS,
  type FilterState,
  type SortKey,
} from '../components/signshop/SignShopFilters';

const CUSTOM_MARKUP_CENTS = 2000; // +$20 for customizable variants

interface PropertyRef {
  id: number | string;
  name?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
}

interface PrintifyVariant {
  id: number;
  price: number;
  title: string;
  is_enabled: boolean;
  is_available: boolean;
  sku: string;
}

interface PrintifyImage {
  src: string;
  is_default: boolean;
  position: string;
  variant_ids?: number[];
}

interface PrintifyProduct {
  id: string;
  title: string;
  description: string;
  tags: string[];
  visible: boolean;
  images: PrintifyImage[];
  variants: PrintifyVariant[];
}

interface CartItem {
  productId: string;
  productTitle: string;
  variantId: number;
  variantTitle: string;
  unitPrice: number;
  quantity: number;
  imageSrc: string;
  propertyId?: number | string;
  propertyLabel?: string;
  intercomId?: number | string;
  intercomLabel?: string;
  // Personalization (Printify mockup generation)
  basePrintifyProductId?: string;     // original catalog product id
  personalizedProductId?: string;     // Printify draft product with custom design
  mockupUrl?: string;                 // photo-realistic preview from Printify
  designData?: { property_name: string; phone: string; qr_target: string };
}

interface ShipAddress {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address1: string;
  city: string;
  region: string;
  zip: string;
  country: string;
}

const CART_KEY = 'intrlock.signshop.cart';
const ADDR_KEY = 'intrlock.signshop.address';

const fmt = (cents: number) => `$${(cents / 100).toFixed(2)}`;

const stripHtml = (html: string) => {
  const plain = html.replace(/<[^>]*>/g, ' ');
  if (typeof document === 'undefined') return plain.replace(/\s+/g, ' ').trim();
  const textarea = document.createElement('textarea');
  textarea.innerHTML = plain;
  return textarea.value.replace(/\s+/g, ' ').trim();
};

const isCatalogProduct = (product: PrintifyProduct): boolean => {
  const tags = Array.isArray((product as any).tags) ? (product as any).tags.map((tag: unknown) => String(tag).toLowerCase()) : [];
  const title = String(product.title || '').toLowerCase();
  return !!product.visible && !tags.includes('intrlock-personalized') && !title.startsWith('intrlock personalized');
};

const priceRange = (variants: PrintifyVariant[]): string => {
  const prices = variants.filter(v => v.is_enabled).map(v => v.price).filter(p => p > 0);
  if (prices.length === 0) return '—';
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return min === max ? fmt(min) : `${fmt(min)} – ${fmt(max)}`;
};

const heroImage = (p: PrintifyProduct, variantId?: number): string => {
  if (variantId) {
    const match = p.images.find(i => i.variant_ids?.includes(variantId));
    if (match) return match.src;
  }
  const def = p.images.find(i => i.is_default);
  return (def || p.images[0])?.src || '';
};

const loadCart = (): CartItem[] => {
  try { return JSON.parse(localStorage.getItem(CART_KEY) || '[]'); } catch { return []; }
};

const loadAddress = (): ShipAddress => {
  try {
    const saved = JSON.parse(localStorage.getItem(ADDR_KEY) || 'null');
    if (saved) return saved;
  } catch {}
  return { first_name: '', last_name: '', email: '', phone: '', address1: '', city: '', region: '', zip: '', country: 'US' };
};

export const SignShop: React.FC = () => {
  const [products, setProducts] = useState<PrintifyProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortKey, setSortKey] = useState<SortKey>('newest');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [selected, setSelected] = useState<PrintifyProduct | null>(null);
  const [selectedVariantId, setSelectedVariantId] = useState<number | null>(null);
  const [selectedQty, setSelectedQty] = useState<number>(1);
  const [view, setView] = useState<'shop' | 'cart' | 'checkout' | 'placed'>('shop');
  const [cart, setCart] = useState<CartItem[]>(loadCart());
  const [address, setAddress] = useState<ShipAddress>(loadAddress());
  const [shipQuote, setShipQuote] = useState<{ standard: number } | null>(null);
  const [shipLoading, setShipLoading] = useState(false);
  const [shipError, setShipError] = useState<string | null>(null);
  const [orderLoading, setOrderLoading] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [placedOrderId, setPlacedOrderId] = useState<string | null>(null);
  const [properties, setProperties] = useState<PropertyRef[]>([]);
  const [intercomsByProperty, setIntercomsByProperty] = useState<Record<string, IntercomConfig[]>>({});
  // Personalization
  const [personalizeMode, setPersonalizeMode] = useState<'quick' | 'custom'>('quick');
  const [modalPropertyId, setModalPropertyId] = useState<string | number | ''>('');
  const [modalIntercomId, setModalIntercomId] = useState<string | number | ''>('');
  const [designPreviewUrl, setDesignPreviewUrl] = useState<string | null>(null);
  const [mockupUrl, setMockupUrl] = useState<string | null>(null);
  const [mockupLoading, setMockupLoading] = useState(false);
  const [mockupError, setMockupError] = useState<string | null>(null);
  const [personalized, setPersonalized] = useState<{
    id: string;
    url: string;
    isCustom: boolean;
    mockups?: Array<{ url: string; label: string; camera: string }>;
  } | null>(null);
  const [designerOpen, setDesignerOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await printify.getProducts();
        const list: PrintifyProduct[] = (res?.data || []).filter(isCatalogProduct);
        if (!cancelled) setProducts(list);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load sign catalog');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    getProperties().then((rows: any[]) => {
      setProperties((rows || []).map(r => ({
        id: r.id, name: r.name, address: r.address, city: r.city, state: r.state, zip: r.zip,
      })));
    }).catch(() => {});
  }, []);

  const ensureIntercomsLoaded = async (propertyId: number | string) => {
    const key = String(propertyId);
    if (intercomsByProperty[key]) return;
    try {
      const list = await getIntercomConfigs(propertyId);
      setIntercomsByProperty(prev => ({ ...prev, [key]: list }));
    } catch {
      setIntercomsByProperty(prev => ({ ...prev, [key]: [] }));
    }
  };

  useEffect(() => {
    if (selected) {
      const first = selected.variants.find(v => v.is_enabled && v.is_available);
      setSelectedVariantId(first?.id || null);
      setSelectedQty(1);
      setModalPropertyId('');
      setModalIntercomId('');
      setDesignPreviewUrl(null);
      setMockupUrl(null);
      setMockupError(null);
      setPersonalized(null);
      setPersonalizeMode('quick');
      setDesignerOpen(false);
    }
  }, [selected]);

  // Switching mode invalidates the prior mockup (different design / different markup).
  useEffect(() => {
    setMockupUrl(null);
    setPersonalized(null);
    setMockupError(null);
  }, [personalizeMode, selectedVariantId]);

  // Quick personalization is rendered server-side from the product's original print artwork.
  // Never create a replacement generic canvas here: that loses the selected design and size.
  useEffect(() => {
    setDesignPreviewUrl(null);
    setMockupUrl(null);
    setPersonalized(null);
    setMockupError(null);
  }, [selected, modalPropertyId, modalIntercomId]);

  const submitPersonalize = async (designPng: string | null, isCustom: boolean) => {
    if (!selected || !selectedVariantId) return;
    setMockupLoading(true);
    setMockupError(null);
    try {
      const prop = properties.find(p => String(p.id) === String(modalPropertyId));
      const ics = intercomsByProperty[String(modalPropertyId)] || [];
      const ic = ics.find(c => String(c.id) === String(modalIntercomId));
      const res = await signShopOrdersApi.personalize({
        base_product_id: selected.id,
        variant_id: selectedVariantId,
        personalization_mode: isCustom ? 'custom' : 'quick',
        ...(designPng ? { image_base64: designPng } : {}),
        property_label: prop?.name || prop?.address || '',
        address_label: prop?.address || prop?.name || '',
        phone: ic?.phone_number || '',
        qr_target: `${visitorOrigin()}/${(prop as any)?.property_uuid || prop?.id || ''}`,
      });
      setMockupUrl(res.mockup_urls[0] || null);
      setPersonalized({
        id: res.personalized_product_id,
        url: res.mockup_urls[0] || '',
        isCustom,
        mockups: res.mockups,
      });
    } catch (e: any) {
      setMockupError(e?.message || 'Mockup generation failed');
    } finally {
      setMockupLoading(false);
    }
  };

  const generateMockup = () => {
    submitPersonalize(null, false);
  };

  const filtered = useMemo(() => {
    return applyFilterSortSearch({
      products: products as any,
      filters,
      search: searchQuery,
      sort: sortKey,
    }) as unknown as PrintifyProduct[];
  }, [products, filters, searchQuery, sortKey]);

  const clearAllFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSearchQuery('');
  };

  const cartCount = cart.reduce((sum, i) => sum + i.quantity, 0);
  const subtotal = cart.reduce((sum, i) => sum + i.unitPrice * i.quantity, 0);

  const addToCart = () => {
    if (!selected || !selectedVariantId || !personalized || !modalPropertyId || !modalIntercomId) return;
    const variant = selected.variants.find(v => v.id === selectedVariantId);
    if (!variant) return;
    const prop = properties.find(p => String(p.id) === String(modalPropertyId));
    const ics = intercomsByProperty[String(modalPropertyId)] || [];
    const ic = ics.find(c => String(c.id) === String(modalIntercomId));
    const markup = personalized.isCustom ? CUSTOM_MARKUP_CENTS : 0;
    setCart(prev => ([
      ...prev,
      {
        productId: personalized.id,
        basePrintifyProductId: selected.id,
        productTitle: `${selected.title}${personalized.isCustom ? ' — Custom design' : ''}`,
        variantId: selectedVariantId,
        variantTitle: variant.title,
        unitPrice: variant.price + markup,
        quantity: selectedQty,
        imageSrc: personalized.url || heroImage(selected, selectedVariantId),
        propertyId: prop?.id,
        propertyLabel: prop ? (prop.name || prop.address || `Property ${prop.id}`) : undefined,
        intercomId: ic?.id,
        intercomLabel: ic?.label,
        personalizedProductId: personalized.id,
        mockupUrl: personalized.url,
        designData: {
          property_name: prop?.name || prop?.address || '',
          phone: ic?.phone_number || '',
          qr_target: `${visitorOrigin()}/${(prop as any)?.property_uuid || prop?.id || ''}`,
        },
      },
    ]));
    setSelected(null);
  };

  const updateCartQty = (idx: number, qty: number) => {
    if (qty < 1) return;
    setCart(prev => prev.map((i, n) => n === idx ? { ...i, quantity: qty } : i));
  };

  const removeFromCart = (idx: number) => {
    setCart(prev => prev.filter((_, n) => n !== idx));
  };

  const setLineProperty = (idx: number, propertyId: string) => {
    const prop = properties.find(p => String(p.id) === propertyId);
    setCart(prev => prev.map((i, n) => n === idx ? {
      ...i,
      propertyId: prop?.id,
      propertyLabel: prop ? (prop.name || prop.address || `Property ${prop.id}`) : undefined,
      intercomId: undefined,
      intercomLabel: undefined,
    } : i));
    if (prop) ensureIntercomsLoaded(prop.id);
  };

  const setLineIntercom = (idx: number, intercomId: string) => {
    const item = cart[idx];
    if (!item?.propertyId) return;
    const list = intercomsByProperty[String(item.propertyId)] || [];
    const ic = list.find(c => String(c.id) === intercomId);
    setCart(prev => prev.map((i, n) => n === idx ? {
      ...i,
      intercomId: ic?.id,
      intercomLabel: ic?.label,
    } : i));
  };

  useEffect(() => {
    cart.forEach(i => { if (i.propertyId) ensureIntercomsLoaded(i.propertyId); });
  }, [cart.length]); // eslint-disable-line

  const allLinesAssigned = cart.length > 0 && cart.every(i => i.propertyId && i.intercomId);

  const fetchShipping = async () => {
    setShipLoading(true);
    setShipError(null);
    setShipQuote(null);
    try {
      const res = await signShopOrdersApi.getShippingQuote({
        cart,
        address: {
          first_name: address.first_name,
          last_name: address.last_name,
          email: address.email,
          phone: address.phone,
          address1: address.address1,
          city: address.city,
          region: address.region,
          zip: address.zip,
          country: address.country,
        },
      });
      setShipQuote({ standard: res.standard || 0 });
    } catch (e: any) {
      setShipError(e?.message || 'Could not get shipping rate');
    } finally {
      setShipLoading(false);
    }
  };

  const placeOrder = async () => {
    setOrderLoading(true);
    setOrderError(null);
    try {
      localStorage.setItem(ADDR_KEY, JSON.stringify(address));
      const { url } = await signShopOrdersApi.createCheckoutSession({
        cart,
        address,
        shipping_cents: shipQuote?.standard ?? 0,
        return_origin: window.location.origin,
      });
      if (!url) throw new Error('Stripe did not return a checkout URL');
      window.location.href = url;
    } catch (e: any) {
      setOrderError(e?.message || 'Checkout failed');
      setOrderLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id') || params.get('paid_session');
    const canceled = params.get('canceled');
    if (canceled) {
      setOrderError('Payment was canceled — your cart was kept.');
      const url = new URL(window.location.href);
      url.searchParams.delete('canceled');
      url.searchParams.delete('order_id');
      window.history.replaceState(null, '', url.toString());
      return;
    }
    if (sessionId) {
      (async () => {
        try {
          const order = await signShopOrdersApi.syncSession(sessionId);
          setPlacedOrderId(order.id);
          setCart([]);
          setShipQuote(null);
          setView('placed');
          const url = new URL(window.location.href);
          url.searchParams.delete('session_id');
          url.searchParams.delete('paid_session');
          url.searchParams.delete('order_id');
          window.history.replaceState(null, '', url.toString());
        } catch (e: any) {
          setOrderError(`Payment received, but order sync failed: ${e?.message || e}`);
        }
      })();
    }
    // eslint-disable-next-line
  }, []);

  const canQuoteShipping = cart.length > 0
    && address.first_name && address.last_name && address.address1
    && address.city && address.region && address.zip && address.country;

  // Top toolbar
  const Toolbar = (
    <div className="max-w-7xl mx-auto mb-6 flex items-center justify-between gap-3">
      <div className="flex gap-2">
        <Button variant={view === 'shop' ? 'primary' : 'secondary'} size="sm" onClick={() => setView('shop')}>Shop</Button>
        <Button variant={view === 'cart' ? 'primary' : 'secondary'} size="sm" onClick={() => setView('cart')}>
          Cart{cartCount > 0 ? ` (${cartCount})` : ''}
        </Button>
      </div>
      {view === 'shop' && (
        <div className="text-sm text-gray-500">
          {loading ? 'Loading…' : `${filtered.length} of ${products.length} products`}
        </div>
      )}
    </div>
  );

  if (view === 'placed') {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-6">
        <div className="max-w-2xl mx-auto mt-12">
          <Card>
            <div className="text-center">
              <div className="text-4xl mb-2">✓</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment received</h2>
              <p className="text-gray-600 mb-2">Order reference: <code className="bg-gray-100 px-2 py-1 rounded">{placedOrderId}</code></p>
              <p className="text-gray-600 mb-6 text-sm">
                Your order is queued for review. It will be submitted for production once an admin approves it.
              </p>
              <div className="flex justify-center gap-2">
                <Button onClick={() => setView('shop')} variant="secondary">Back to shop</Button>
                <Button onClick={() => { window.location.href = '/sign-shop-orders'; }}>View orders</Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (view === 'cart' || view === 'checkout') {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-6">
        <div className="max-w-7xl mx-auto mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Sign Shop</h1>
        </div>
        {Toolbar}

        <div className="max-w-4xl mx-auto space-y-4">
          <Card>
            <h2 className="text-lg font-semibold mb-4">Cart</h2>
            {cart.length === 0 ? (
              <div className="text-gray-500 py-6 text-center">Your cart is empty.</div>
            ) : (
              <div className="divide-y">
                {cart.map((item, idx) => {
                  const intercoms = item.propertyId ? (intercomsByProperty[String(item.propertyId)] || []) : [];
                  const needsAssignment = !item.propertyId || !item.intercomId;
                  return (
                    <div key={`${item.productId}-${item.variantId}-${idx}`} className="py-4 space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="w-16 h-16 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                          {item.imageSrc && <img src={item.imageSrc} alt={item.productTitle} className="w-full h-full object-cover" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 truncate">{item.productTitle}</div>
                          <div className="text-sm text-gray-500">{item.variantTitle}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateCartQty(idx, item.quantity - 1)}
                            className="w-7 h-7 border border-gray-300 rounded hover:bg-gray-50"
                          >−</button>
                          <span className="w-8 text-center">{item.quantity}</span>
                          <button
                            onClick={() => updateCartQty(idx, item.quantity + 1)}
                            className="w-7 h-7 border border-gray-300 rounded hover:bg-gray-50"
                          >+</button>
                        </div>
                        <div className="w-20 text-right font-medium">{fmt(item.unitPrice * item.quantity)}</div>
                        <button
                          onClick={() => removeFromCart(idx)}
                          className="text-red-500 hover:text-red-700 px-2"
                          aria-label="Remove"
                        >✕</button>
                      </div>

                      <div className="ml-19 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Property</label>
                          <select
                            value={item.propertyId ? String(item.propertyId) : ''}
                            onChange={e => setLineProperty(idx, e.target.value)}
                            className={`w-full border rounded px-2 py-1.5 text-sm ${!item.propertyId ? 'border-amber-400 bg-amber-50' : 'border-gray-300'}`}
                          >
                            <option value="">Select property…</option>
                            {properties.map(p => (
                              <option key={String(p.id)} value={String(p.id)}>
                                {p.name || p.address || `Property ${p.id}`}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Intercom</label>
                          <select
                            value={item.intercomId ? String(item.intercomId) : ''}
                            onChange={e => setLineIntercom(idx, e.target.value)}
                            disabled={!item.propertyId}
                            className={`w-full border rounded px-2 py-1.5 text-sm disabled:bg-gray-100 disabled:text-gray-400 ${item.propertyId && !item.intercomId ? 'border-amber-400 bg-amber-50' : 'border-gray-300'}`}
                          >
                            <option value="">
                              {!item.propertyId ? 'Pick a property first' : intercoms.length === 0 ? 'No intercoms found' : 'Select intercom…'}
                            </option>
                            {intercoms.map(c => (
                              <option key={String(c.id)} value={String(c.id)}>
                                {c.label || `Intercom ${c.id}`}{c.phone_number ? ` — ${c.phone_number}` : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      {needsAssignment && (
                        <div className="ml-19 text-xs text-amber-700">
                          Assign this sign to a property and intercom before placing the order.
                        </div>
                      )}
                    </div>
                  );
                })}
                <div className="pt-3 flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-semibold">{fmt(subtotal)}</span>
                </div>
              </div>
            )}
          </Card>

          {cart.length > 0 && (
            <Card>
              <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <h2 className="text-lg font-semibold">Shipping address</h2>
                <div className="flex gap-2 flex-wrap">
                  {(() => {
                    const propIds = Array.from(new Set(cart.map(i => i.propertyId).filter(Boolean))) as (string | number)[];
                    const propsInCart = propIds
                      .map(id => properties.find(p => String(p.id) === String(id)))
                      .filter((p): p is PropertyRef => !!p && !!p.address);
                    if (propsInCart.length === 0) return null;
                    return propsInCart.map(p => (
                      <Button
                        key={String(p.id)}
                        size="sm"
                        variant="secondary"
                        onClick={() => setAddress(a => ({
                          ...a,
                          address1: p.address || a.address1,
                          city: p.city || a.city,
                          region: p.state || a.region,
                          zip: p.zip || a.zip,
                          country: a.country || 'US',
                        }))}
                      >
                        Use {p.name || p.address}
                      </Button>
                    ));
                  })()}
                </div>
              </div>
              <form autoComplete="on" onSubmit={e => e.preventDefault()} aria-label="Shipping address">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input id="ship-fname" className="border rounded px-3 py-2" placeholder="First name" autoComplete="given-name" name="fname" value={address.first_name} onChange={e => setAddress({ ...address, first_name: e.target.value })} />
                  <input id="ship-lname" className="border rounded px-3 py-2" placeholder="Last name" autoComplete="family-name" name="lname" value={address.last_name} onChange={e => setAddress({ ...address, last_name: e.target.value })} />
                  <input id="ship-email" className="border rounded px-3 py-2 sm:col-span-2" type="email" placeholder="Email" autoComplete="email" name="email" value={address.email} onChange={e => setAddress({ ...address, email: e.target.value })} />
                  <input id="ship-tel" className="border rounded px-3 py-2 sm:col-span-2" type="tel" placeholder="Phone" autoComplete="tel" name="tel" value={address.phone} onChange={e => setAddress({ ...address, phone: e.target.value })} />
                  <div className="sm:col-span-2 -mb-4">
                    <AddressAutocomplete
                      label=""
                      placeholder="Start typing an address…"
                      value={address.address1}
                      onChange={(street) => setAddress(a => ({ ...a, address1: street }))}
                      onSelect={(p) => setAddress(a => ({
                        ...a,
                        address1: p.street || a.address1,
                        city: p.city || a.city,
                        region: p.state || a.region,
                        zip: p.zip || a.zip,
                        country: a.country || 'US',
                      }))}
                    />
                  </div>
                  <input id="ship-city" className="border rounded px-3 py-2" placeholder="City" autoComplete="address-level2" name="city" value={address.city} onChange={e => setAddress({ ...address, city: e.target.value })} />
                  <input id="ship-state" className="border rounded px-3 py-2" placeholder="State (e.g. CA)" autoComplete="address-level1" name="state" value={address.region} onChange={e => setAddress({ ...address, region: e.target.value })} />
                  <input id="ship-zip" className="border rounded px-3 py-2" placeholder="ZIP" autoComplete="postal-code" name="zip" value={address.zip} onChange={e => setAddress({ ...address, zip: e.target.value })} />
                  <input id="ship-country" className="border rounded px-3 py-2" placeholder="Country (US)" autoComplete="country" name="country" value={address.country} onChange={e => setAddress({ ...address, country: e.target.value })} />
                </div>
              </form>

              <div className="mt-4 flex items-center gap-3">
                <Button onClick={fetchShipping} disabled={!canQuoteShipping} loading={shipLoading} variant="secondary">
                  Get shipping rate
                </Button>
                {shipQuote && <span className="text-sm">Standard shipping: <strong>{fmt(shipQuote.standard)}</strong></span>}
                {shipError && <span className="text-sm text-red-600">{shipError}</span>}
              </div>

              {shipQuote && (
                <div className="mt-4 pt-4 border-t">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Subtotal</span><span>{fmt(subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Shipping</span><span>{fmt(shipQuote.standard)}</span>
                  </div>
                  <div className="flex justify-between text-base font-semibold">
                    <span>Total</span><span>{fmt(subtotal + shipQuote.standard)}</span>
                  </div>
                  <div className="mt-4 flex justify-end gap-2">
                    {orderError && <div className="text-sm text-red-600 self-center">{orderError}</div>}
                    <Button onClick={placeOrder} loading={orderLoading} disabled={!allLinesAssigned}>
                      {allLinesAssigned ? 'Pay with Stripe' : 'Assign property + intercom first'}
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          )}
        </div>
      </div>
    );
  }

  const detailVariants = selected?.variants.filter(v => v.is_enabled) || [];
  const detailVariant = detailVariants.find(v => v.id === selectedVariantId);

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Sign Shop</h1>
          <p className="text-gray-500 mt-1">Signs available to order for your properties.</p>
        </div>
      </div>

      {Toolbar}

      {error && (
        <div className="max-w-7xl mx-auto mb-6">
          <Card>
            <div className="text-red-700">
              <div className="font-semibold mb-1">Couldn't load sign catalog</div>
              <div className="text-sm">{error}</div>
            </div>
          </Card>
        </div>
      )}

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
        {/* Desktop sidebar */}
        {!loading && products.length > 0 && (
          <div className="hidden lg:block">
            <SignShopFilters
              products={products as any}
              filters={filters}
              search={searchQuery}
              sort={sortKey}
              onFiltersChange={setFilters}
              onSearchChange={setSearchQuery}
              onSortChange={setSortKey}
            />
          </div>
        )}

        <div className="min-w-0">
          {!loading && products.length > 0 && (
            <div className="mb-4">
              <SignShopToolbar
                search={searchQuery}
                onSearchChange={setSearchQuery}
                sort={sortKey}
                onSortChange={setSortKey}
                filters={filters}
                onFiltersChange={setFilters}
                resultCount={filtered.length}
                totalCount={products.length}
                onOpenMobileFilters={() => setMobileFiltersOpen(true)}
              />
            </div>
          )}

          {loading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              {[...Array(8)].map((_, i) => (
                <Card key={i} padding={false} className="overflow-hidden">
                  <div className="aspect-square bg-gray-100 animate-pulse" />
                  <div className="p-4 space-y-2">
                    <div className="h-4 bg-gray-100 rounded animate-pulse" />
                    <div className="h-3 w-2/3 bg-gray-100 rounded animate-pulse" />
                  </div>
                </Card>
              ))}
            </div>
          )}

          {!loading && !error && filtered.length === 0 && (
            <SignShopEmptyState onClear={clearAllFilters} />
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {!loading && filtered.map(p => {
          const img = heroImage(p);
          const enabled = p.variants.filter(v => v.is_enabled).length;
          return (
            <Card key={p.id} padding={false} className="overflow-hidden cursor-pointer">
              <button type="button" onClick={() => setSelected(p)} className="block w-full text-left">
                <div className="aspect-square bg-gray-50 flex items-center justify-center overflow-hidden">
                  {img ? <img src={img} alt={p.title} className="w-full h-full object-cover" loading="lazy" /> : <div className="text-gray-400 text-sm">No image</div>}
                </div>
                <div className="p-4">
                  <div className="font-semibold text-gray-900 line-clamp-2 min-h-[2.75rem]">{p.title}</div>
                  <div className="mt-2 flex items-center justify-between">
                    <div className="text-emerald-700 font-semibold">{priceRange(p.variants)}</div>
                    <Badge color="gray">{enabled} variant{enabled === 1 ? '' : 's'}</Badge>
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    Custom design available <span className="text-emerald-700 font-medium">+{fmt(CUSTOM_MARKUP_CENTS)}</span>
                  </div>
                </div>
              </button>
            </Card>
          );
        })}
          </div>
        </div>
      </div>

      {/* Mobile filter drawer */}
      {mobileFiltersOpen && !loading && products.length > 0 && (
        <div className="lg:hidden fixed inset-0 z-40 flex" onClick={() => setMobileFiltersOpen(false)}>
          <div className="flex-1 bg-black/40" />
          <div
            className="w-80 max-w-[85vw] bg-white h-full overflow-y-auto p-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-gray-900">Filters</div>
              <button
                type="button"
                onClick={() => setMobileFiltersOpen(false)}
                className="text-gray-500 hover:text-gray-800"
                aria-label="Close filters"
              >✕</button>
            </div>
            <SignShopFilters
              variant="inline"
              products={products as any}
              filters={filters}
              search={searchQuery}
              sort={sortKey}
              onFiltersChange={setFilters}
              onSearchChange={setSearchQuery}
              onSortChange={setSortKey}
            />
            <div className="mt-4 flex justify-end">
              <Button onClick={() => setMobileFiltersOpen(false)}>Show {filtered.length} results</Button>
            </div>
          </div>
        </div>
      )}

      {selected && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
              <div className="bg-gray-50 flex flex-col relative">
                <div className="aspect-square flex items-center justify-center">
                  {mockupUrl ? (
                    <img src={mockupUrl} alt="Personalized mockup" className="w-full h-full object-contain" />
                  ) : heroImage(selected, selectedVariantId || undefined) ? (
                    <img src={heroImage(selected, selectedVariantId || undefined)} alt={selected.title} className="w-full h-full object-contain" />
                  ) : <div className="text-gray-400">No image</div>}
                  {designPreviewUrl && !mockupUrl && (
                    <div className="absolute bottom-3 right-3 w-28 rounded-lg border border-gray-200 bg-white p-1.5 shadow-lg">
                      <img src={designPreviewUrl} alt="Personalized print artwork preview" className="aspect-square w-full rounded object-contain" />
                      <div className="mt-1 text-center text-[10px] leading-tight text-gray-500">Print artwork</div>
                    </div>
                  )}
                  {mockupLoading && (
                    <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                      <div className="text-sm text-gray-700">Rendering on the actual product… (15–45 sec)</div>
                    </div>
                  )}
                </div>
                {personalized?.mockups && personalized.mockups.length > 1 && (
                  <div className="px-3 pb-3 grid grid-cols-3 gap-1.5">
                    {personalized.mockups.map(m => (
                      <button
                        key={m.url}
                        onClick={() => { setMockupUrl(m.url); setPersonalized(p => p ? { ...p, url: m.url } : p); }}
                        className={`relative rounded border-2 overflow-hidden transition ${
                          m.url === mockupUrl ? 'border-emerald-600 ring-1 ring-emerald-200' : 'border-transparent hover:border-gray-300'
                        }`}
                      >
                        <img src={m.url} alt={m.label} className="w-full aspect-square object-cover" />
                        <div className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[10px] py-0.5 text-center">{m.label}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="p-6 flex flex-col">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <h2 className="text-xl font-bold text-gray-900">{selected.title}</h2>
                  <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-700">✕</button>
                </div>

                <div className="mb-3">
                  <DesignStartChooser
                    value={personalizeMode === 'quick' ? 'preconfigured' : 'custom'}
                    templateImageUrl={heroImage(selected, selectedVariantId ?? undefined)}
                    onChange={mode => setPersonalizeMode(mode === 'preconfigured' ? 'quick' : 'custom')}
                  />
                </div>

                <div className="text-emerald-700 font-semibold mb-3">
                  {detailVariant
                    ? fmt(detailVariant.price + (personalizeMode === 'custom' ? CUSTOM_MARKUP_CENTS : 0))
                    : priceRange(selected.variants)}
                </div>
                {selected.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-4">
                    {selected.tags.map(t => <Badge key={t} color="gray">{t}</Badge>)}
                  </div>
                )}
                <p className="text-sm text-gray-700 mb-4 whitespace-pre-line line-clamp-6">
                  {stripHtml(selected.description)}
                </p>

                <div className="border-t pt-4">
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Variant ({detailVariants.length} available)
                  </label>
                  <select
                    value={selectedVariantId ?? ''}
                    onChange={e => setSelectedVariantId(Number(e.target.value))}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  >
                    {detailVariants.map(v => (
                      <option key={v.id} value={v.id} disabled={!v.is_available}>
                        {v.title} — {fmt(v.price)}{!v.is_available ? ' (out of stock)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Property *</label>
                    <select
                      value={String(modalPropertyId)}
                      onChange={e => {
                        setModalPropertyId(e.target.value);
                        setModalIntercomId('');
                        if (e.target.value) ensureIntercomsLoaded(e.target.value);
                      }}
                      className={`w-full border rounded px-2 py-1.5 text-sm ${!modalPropertyId ? 'border-amber-400 bg-amber-50' : 'border-gray-300'}`}
                    >
                      <option value="">Select…</option>
                      {properties.map(p => (
                        <option key={String(p.id)} value={String(p.id)}>
                          {p.name || p.address || `Property ${p.id}`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Intercom *</label>
                    <select
                      value={String(modalIntercomId)}
                      onChange={e => setModalIntercomId(e.target.value)}
                      disabled={!modalPropertyId}
                      className={`w-full border rounded px-2 py-1.5 text-sm disabled:bg-gray-100 ${modalPropertyId && !modalIntercomId ? 'border-amber-400 bg-amber-50' : 'border-gray-300'}`}
                    >
                      <option value="">
                        {!modalPropertyId ? 'Pick property first' : 'Select…'}
                      </option>
                      {(intercomsByProperty[String(modalPropertyId)] || []).map(c => (
                        <option key={String(c.id)} value={String(c.id)}>
                          {c.label || `Intercom ${c.id}`}{c.phone_number ? ` — ${c.phone_number}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-semibold text-gray-900 mb-2">Quantity</label>
                  <div className="flex items-center gap-3">
                    <button onClick={() => setSelectedQty(q => Math.max(1, q - 1))} className="w-8 h-8 border border-gray-300 rounded hover:bg-gray-50">−</button>
                    <input type="number" min={1} value={selectedQty}
                      onChange={e => setSelectedQty(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-16 text-center border border-gray-300 rounded px-2 py-1" />
                    <button onClick={() => setSelectedQty(q => q + 1)} className="w-8 h-8 border border-gray-300 rounded hover:bg-gray-50">+</button>
                  </div>
                </div>

                {personalizeMode === 'custom' && (
                  !modalPropertyId || !modalIntercomId ? (
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
                      Select a property and intercom above to open the build-your-own designer.
                    </div>
                  ) : (
                    <div className="mt-4">
                      <Button onClick={() => setDesignerOpen(true)} variant="secondary" className="w-full">
                        {personalized?.isCustom ? '✎ Reopen build-your-own designer' : '✎ Open build-your-own designer →'}
                      </Button>
                    </div>
                  )
                )}

                {mockupError && (
                  <div className="mt-3 text-xs text-red-700">{mockupError}</div>
                )}

                <div className="mt-auto pt-6 flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-sm text-gray-600">
                    Line total: <span className="font-semibold text-gray-900">
                      {detailVariant
                        ? fmt((detailVariant.price + (personalizeMode === 'custom' ? CUSTOM_MARKUP_CENTS : 0)) * selectedQty)
                        : '—'}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => setSelected(null)} variant="secondary">Cancel</Button>
                    {personalizeMode === 'quick' && !personalized && (
                      <Button
                        onClick={generateMockup}
                        loading={mockupLoading}
                        disabled={!selectedVariantId || !modalPropertyId || !modalIntercomId}
                      >
                        {modalPropertyId && modalIntercomId ? 'Generate mockup' : 'Pick property + intercom'}
                      </Button>
                    )}
                    {personalized && (
                      <Button onClick={addToCart}>
                        Add to cart — {fmt((detailVariant?.price || 0) + (personalized.isCustom ? CUSTOM_MARKUP_CENTS : 0))}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Full-screen Custom Designer Modal ──────────────────────────── */}
      {designerOpen && selected && detailVariant && (() => {
        const aspect = parseVariantAspect(detailVariant.title) || { w: 1, h: 1 };
        const prop = properties.find(p => String(p.id) === String(modalPropertyId));
        const ics = intercomsByProperty[String(modalPropertyId)] || [];
        const ic = ics.find(c => String(c.id) === String(modalIntercomId));
        const qrTarget = prop
          ? `${visitorOrigin()}/${(prop as any).property_uuid || prop.id}`
          : 'https://intrlock.io';
        return (
          <div className="fixed inset-0 bg-black/60 z-[60] flex flex-col">
            <div className="bg-white border-b px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-gray-900 truncate">Custom designer — {selected.title}</h2>
                <div className="text-xs text-gray-500">
                  {detailVariant.title} · {prop?.name || prop?.address} · {ic?.label}
                  {' · '}<span className="text-emerald-700 font-medium">{fmt((detailVariant.price + CUSTOM_MARKUP_CENTS) * selectedQty)}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {mockupUrl && (
                  <Button onClick={() => { addToCart(); setDesignerOpen(false); }}>
                    Add to cart — {fmt((detailVariant.price + CUSTOM_MARKUP_CENTS) * selectedQty)}
                  </Button>
                )}
                <Button variant="secondary" onClick={() => setDesignerOpen(false)}>Close</Button>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-gray-100">
              <div className="max-w-7xl mx-auto p-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 bg-white rounded-lg shadow p-4">
                  <SignDesigner
                    aspect={aspect}
                    defaultQrTarget={qrTarget}
                    defaultPropertyName={prop?.name || prop?.address}
                    defaultPhone={ic?.phone_number}
                    onExport={(png) => submitPersonalize(png, true)}
                    exportLoading={mockupLoading}
                    exportingLabel={mockupUrl ? 'Regenerate mockup' : 'Generate mockup'}
                  />
                </div>
                <div className="bg-white rounded-lg shadow p-4 space-y-3">
                  <div className="text-sm font-semibold text-gray-900">Live mockup preview</div>
                  {mockupLoading && (
                    <div className="aspect-square bg-gray-100 rounded flex items-center justify-center text-sm text-gray-600 text-center p-4">
                      Rendering on the actual product…<br />(15–45 sec)
                    </div>
                  )}
                  {!mockupLoading && mockupUrl && (
                    <>
                      <img src={mockupUrl} alt="Mockup" className="w-full rounded border border-gray-200" />
                      {personalized?.mockups && personalized.mockups.length > 1 && (
                        <>
                          <div className="text-xs text-gray-500">
                            Tap an angle to use it as the cart thumbnail
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            {personalized.mockups.map(m => (
                              <button
                                key={m.url}
                                onClick={() => {
                                  setMockupUrl(m.url);
                                  setPersonalized(p => p ? { ...p, url: m.url } : p);
                                }}
                                className={`relative rounded border-2 overflow-hidden transition ${
                                  m.url === mockupUrl ? 'border-emerald-600 ring-2 ring-emerald-200' : 'border-transparent hover:border-gray-300'
                                }`}
                              >
                                <img src={m.url} alt={m.label} className="w-full aspect-square object-cover" />
                                <div className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[10px] font-medium py-0.5 text-center">
                                  {m.label}
                                </div>
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                      <div className="text-xs text-gray-500">
                        This is what will be produced. Click "Add to cart" in the header to commit.
                      </div>
                    </>
                  )}
                  {!mockupLoading && !mockupUrl && (
                    <div className="aspect-square bg-gray-50 rounded flex items-center justify-center text-sm text-gray-400 text-center p-4">
                      Build your design, then click "Generate mockup" to preview on the actual product.
                    </div>
                  )}
                  {mockupError && (
                    <div className="text-xs text-red-700">{mockupError}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};
